/** @jsx jsx */
import { css, jsx } from '@emotion/react';

import * as React from 'react';

import { Accordion, AccordionPanel, Button } from '@coursera/cds-core';
import type { AccordionProps } from '@coursera/cds-core';
import { ChevronNextIcon } from '@coursera/cds-icons';

import useIsMobile from 'bundles/unified-common/hooks/useIsMobile';
import AccordionHeader from 'bundles/unified-description-page-common/components/accordions/HeaderButtonRedesign';
import type { CourseItemContentElement } from 'bundles/unified-description-page-common/components/modules/syllabus/CourseItemContent';
import type { CourseItemSubtitleElement } from 'bundles/unified-description-page-common/components/modules/syllabus/CourseItemSubtitle';
import type { SyllabusItemTitleElement } from 'bundles/unified-description-page-common/components/modules/syllabus/SyllabusItemTitle';
import Modal, { useModal } from 'bundles/unified-description-page-common/components/shared/modals/Modal';
import xdpStyles from 'bundles/unified-description-page-common/constants/styles';

const styles = {
  accordion: (hasDivider: boolean) => css`
    position: relative;
    padding: 0;
    margin: 0;
    ${hasDivider ? `margin-top: -8px;` : ''}

    &::before {
      ${hasDivider ? xdpStyles.border('border-top') : 'border-top: none;'}
      content: '';
      position: absolute;
      top: 0;
      height: 2px;
      left: var(--cds-spacing-200);
      right: var(--cds-spacing-200);
    }
  `,
  panel: css`
    margin: 0;
    padding: 0 var(--cds-spacing-200) var(--cds-spacing-200) var(--cds-spacing-200);
  `,
  button: css`
    justify-content: space-between;
    text-align: left;
    border-radius: 0;

    /* hide the focus ring that overlaps adjacent buttons */
    overflow: hidden;
  `,
  mobileSyllabusItem: css`
    & + & {
      ${xdpStyles.border('border-top')}
    }
  `,
  headerContent: css`
    display: flex;
    gap: var(--cds-spacing-200);
    min-width: 0;
  `,
  titleContent: css`
    min-width: 0;
  `,
};

interface Props {
  children: [SyllabusItemTitleElement, CourseItemSubtitleElement, CourseItemContentElement];
  itemIndex: number;
  thumbnail?: React.ReactNode;
  toggleButtonLabel: string;
}

function SyllabusItem({
  children,
  itemIndex,
  thumbnail,
  toggleButtonLabel,
}: Props): React.ReactElement<AccordionProps | 'div'> {
  const isMobile = useIsMobile();
  const { isModalOpen, setModalOpen } = useModal();

  const [title, subtitle, panel] = React.Children.toArray(children) as [
    SyllabusItemTitleElement,
    CourseItemSubtitleElement,
    CourseItemContentElement
  ];

  if (isMobile) {
    const modalHeader = React.cloneElement(title, { headingLevel: 'h2' });

    return (
      <div css={styles.mobileSyllabusItem}>
        <Button
          variant="ghost"
          icon={<ChevronNextIcon size="medium" />}
          css={styles.button}
          fullWidth
          onClick={setModalOpen}
        >
          <div css={styles.headerContent}>
            {thumbnail}
            <div>
              {React.cloneElement(title, { disableLink: true })}
              {subtitle}
            </div>
          </div>
        </Button>
        <Modal open={isModalOpen} setOpen={setModalOpen} header={modalHeader} hideActions>
          {panel}
        </Modal>
      </div>
    );
  }

  return (
    <Accordion variant="silent" css={styles.accordion(itemIndex > 1)}>
      <AccordionHeader label={toggleButtonLabel}>
        <div css={styles.headerContent}>
          {thumbnail}
          <div css={styles.titleContent}>
            {title}
            {subtitle}
          </div>
        </div>
      </AccordionHeader>
      <AccordionPanel css={styles.panel}>{panel}</AccordionPanel>
    </Accordion>
  );
}

export default SyllabusItem;
